import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  Slide,
  Box,
  Typography,
  Button,
  List,
  ListItem,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useAccount } from './../../../contexts/Account';
import Step1 from './Step1';
import FreemiumStep2 from './Step2';
import eventBus, { eventBusValues } from './../../../eventBus';
import { getCompanyRecurlyPlan } from './../../../services/company_services';
import { getAdvisorPlanUpgrades } from './../../../services/advisor_services';
import AdvisoryCloudPlusOnboarding from '../../../hooks/AdvisoryCloudPlusOnboarding';
import { sendFSEventUpgradeViewed } from '../../../services/FullStory';

const CompanyFreemiumModal = () => {
  const {
    accountDetails,
    upgradeFreemiumAccount,
    currentPlan,
    type,
    currentUserInfo,
    asLoggedUserDetails,
  } = useAccount();
  const location = useLocation();

  const [openModal, setOpenModal] = useState(false);
  const [step1, setStep1] = useState(true);
  const [step2, setStep2] = useState(false);
  const [step3, setStep3] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [acPlusUpgradeOptionOnePlan, setAcPlusUpgradeOptionOnePlan] =
    useState({});
  const [freemiumUpgradeOnePlan, setFreemiumUpgradeOnePlan] = useState({});
  const [acPlusUpgradeOptionTwoPlan, setAcPlusUpgradeOptionTwoPlan] =
    useState({});
  const [freemiumUpgradeTwoPlan, setFreemiumUpgradeTwoPlan] = useState({});
  const [planData, setPlanData] = useState({});
  const [hideBack, setHideBack] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [isUpgrading, setIsUpgrading] = useState(false);

  const handleContinue = () => {
    setStep1(false);
  };

  const handleContinueStep3 = () => {
    setStep1(false);
    setStep2(true);
    setStep3(false);
  };

  const handleContinueStep4 = () => {
    setStep1(false);
    setStep2(false);
    setStep3(true);
  };

  const handleBack = () => {
    setHideBack(false);
    setStep1(true);
  };

  const handleUpgradeComplete = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  function handleClose() {
    if (isUpgrading) {
      return;
    }
    setOpenModal(false);
    setStep1(true);
    setHideBack(false);
    if (step2 || step3) {
      upgradeFreemiumAccount(selectedPlan, 'start_your_advisorycloud');
      handleUpgradeComplete();
    }
  }

  const sendFSViewedEvent = (upgradeAttempted = false) => {
    sendFSEventUpgradeViewed({
      accountDetails,
      currentPlan,
      asLoggedUserDetails,
      currentUserInfo,
      source: location?.state?.fromBanner ? 'banner' : 'gate',
      type,
      upgradeType: 'freemium',
      upgradeAttempted,
    });
  };

  const handleOptionChange = (selection) => {
    setPlanData(selection);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const planResponses = await getCompanyRecurlyPlan({
          recurlyPlanCode: accountDetails.recurlyPlanCode,
        });

        const {
          acPlusUpgradeOptionOne,
          acPlusUpgradeOptionTwo,
          freemiumUpgradeOne,
          freemiumUpgradeTwo,
        } = planResponses.data.getRecurlyPlan;

        if (acPlusUpgradeOptionOne) {
          const planResponsesMonthPlus = await getAdvisorPlanUpgrades({
            RECURLY_PLAN_CODE: acPlusUpgradeOptionOne,
          });
          setAcPlusUpgradeOptionOnePlan(
            planResponsesMonthPlus.data.getFreeTrialUpgrades[0]
          );
        }
        if (acPlusUpgradeOptionTwo) {
          const planResponsesAnnualPlus = await getAdvisorPlanUpgrades({
            RECURLY_PLAN_CODE: acPlusUpgradeOptionTwo,
          });
          setAcPlusUpgradeOptionTwoPlan(
            planResponsesAnnualPlus.data.getFreeTrialUpgrades[0]
          );
        }
        if (freemiumUpgradeOne) {
          const planResponsesMonthManaged = await getAdvisorPlanUpgrades({
            RECURLY_PLAN_CODE: freemiumUpgradeOne,
          });
          setFreemiumUpgradeOnePlan(
            planResponsesMonthManaged.data.getFreeTrialUpgrades[0]
          );
        }
        if (freemiumUpgradeTwo) {
          const planResponsesAnnualManaged = await getAdvisorPlanUpgrades({
            RECURLY_PLAN_CODE: freemiumUpgradeTwo,
          });
          setFreemiumUpgradeTwoPlan(
            planResponsesAnnualManaged.data.getFreeTrialUpgrades[0]
          );
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    }
    eventBus.on(eventBusValues.triggerCompanyUpgradeAction, (data) => {
      if (data && data.planCode === 'managed') {
        setPlanData(freemiumUpgradeOnePlan);
        handleContinue();
        setIsFirstLoad(false);
        setHideBack(true);
      }
      if (data && data.planCode === 'plus') {
        setPlanData(acPlusUpgradeOptionOnePlan);
        handleContinue();
        setIsFirstLoad(false);
        setHideBack(true);
      }
      if (data && data.action) {
        setPendingAction(() => data.action);
      }
      setOpenModal(true);
    });

    const profileParam = new URLSearchParams(location.search).get(
      'b2b_freemium_upgrade_popup'
    );

    if (profileParam) {
      if (profileParam === 'step1') {
        setIsFirstLoad(true);
      } else if (profileParam === 'mab') {
        setPlanData(freemiumUpgradeOnePlan);
        setIsFirstLoad(false);
        handleContinue();
        setHideBack(true);
      } else if (profileParam === 'sya') {
        setPlanData(acPlusUpgradeOptionOnePlan);
        setIsFirstLoad(false);
        handleContinue();
        setHideBack(true);
      }
      setOpenModal(true);
    }

    fetchData();
    return () => {
      eventBus.remove(eventBusValues.triggerCompanyUpgradeAction);
    };
  }, []);

  return (
    <div>
      <Dialog
        open={openModal}
        fullScreen
        scroll="paper"
        disableEscapeKeyDown={isUpgrading}
        onClose={() => {
          handleClose();
          sendFSViewedEvent();
        }}
        PaperProps={{
          id: 'companyFreemiumUpgradeModal',
          sx: {
            borderRadius: 0,
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
            height: '100%',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            background:
              'linear-gradient(180deg, rgba(255, 255, 255, 0.90) 15%, #D3DEFF 45%, #D9F1FF 75%, rgba(255, 255, 255, 0.90) 92%)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xxs: '0px', sm: '40px' },
            overflowY: step1 ? 'hidden' : 'auto',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            cursor: 'pointer',
            zIndex: 10,
          }}
        >
          <Clear
            onClick={() => {
              handleClose();
              sendFSViewedEvent();
            }}
          />
        </Box>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: '1024px',
            minHeight: { xxs: '100%', sm: '620px' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflowX: 'hidden',
            overflowY: 'visible',
            px: { xxs: '16px', sm: 0 },
          }}
        >
          <Slide direction="right" in={step1} mountOnEnter unmountOnExit>
            <Box
              sx={{
                width: '100%',
                position: 'absolute',
                top: 0,
                left: step1 ? 0 : '-100vw',
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: step1 ? 'auto' : 'none',
              }}
            >
              <Step1
                onContinue={() => {
                  handleContinue();
                  setHideBack(false);
                  setIsFirstLoad(false);
                }}
                onOptionChange={handleOptionChange}
                onClose={() => {
                  handleClose();
                  sendFSViewedEvent();
                }}
                freemiumUpgradeOnePlan={freemiumUpgradeOnePlan}
                freemiumUpgradeTwoPlan={freemiumUpgradeTwoPlan}
                acPlusUpgradeOptionOnePlan={acPlusUpgradeOptionOnePlan}
              />
            </Box>
          </Slide>

          <Slide
            direction="left"
            in={!step1 && !step2 && !step3}
            mountOnEnter
            unmountOnExit
            timeout={800}
          >
            <Box
              sx={{
                width: '100%',
                position: 'absolute',
                top: 0,
                left: step1 ? '100vw' : 0,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: step1 ? 'none' : 'auto',
                // pt: { xxs: '24px', sm: '40px' },
                // pb: { xxs: '32px', sm: '60px' },
              }}
            >
              <FreemiumStep2
                planData={planData}
                goBack={handleBack}
                closeModal={handleClose}
                month1={acPlusUpgradeOptionOnePlan}
                month2={freemiumUpgradeOnePlan}
                annual1={acPlusUpgradeOptionTwoPlan}
                annual2={freemiumUpgradeTwoPlan}
                hideBack={hideBack}
                pendingAction={pendingAction}
                handleContinueStep3={handleContinueStep3}
                handleContinueStep4={handleContinueStep4}
                setSelectedPlanFinal={setSelectedPlan}
                isUpgrading={isUpgrading}
                setIsUpgrading={setIsUpgrading}
              />
            </Box>
          </Slide>

          <Slide
            direction="left"
            in={step2}
            mountOnEnter
            unmountOnExit
            timeout={800}
          >
            <Box
              sx={{
                width: '100%',
                position: 'absolute',
                top: 0,
                left: step2 ? 0 : '100vw',
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: step2 ? 'auto' : 'none',
                pt: { xxs: '60px', sm: '120px' },
                pb: { xxs: '60px', sm: '120px' },
              }}
            >
              <AdvisoryCloudPlusOnboarding />
            </Box>
          </Slide>

          <Slide
            direction="left"
            in={step3}
            mountOnEnter
            unmountOnExit
            timeout={800}
          >
            <Box
              sx={{
                width: '100%',
                position: 'absolute',
                top: 0,
                left: step3 ? 0 : '100vw',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: step3 ? 'auto' : 'none',
              }}
            >
              <Box
                sx={{
                  my: { xxs: '140px', sm: '220px' },
                  textAlign: 'center',
                  mx: 'auto',
                  maxWidth: '520px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '18px',
                  boxShadow: '0px 12px 32px rgba(48, 94, 255, 0.12)',
                  p: { xxs: '32px', sm: '48px' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xxs: '22px', sm: '24px' },
                    fontWeight: 700,
                    color: '#3171F6',
                    mb: { xxs: 2, sm: 3 },
                  }}
                  gutterBottom
                >
                  Success, your advisory board has been upgraded!
                </Typography>
                <List
                  sx={{
                    textAlign: 'left',
                    mt: 1,
                    mb: 3,
                    fontSize: '18px',
                    color: '#151F33',
                    px: 2,
                  }}
                >
                  <ListItem sx={{ display: 'list-item', pl: 2 }}>
                    1. You can now invite top advisors to your board
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', pl: 2 }}>
                    2. Make sure your Profile is updated to attract additional
                    interest in our Board Directory
                  </ListItem>
                </List>

                <Button
                  sx={{
                    borderWidth: '2px',
                    padding: '15px 50px',
                    textTransform: 'none',
                    height: '45px',
                    borderRadius: '40px',
                    borderColor: '#3171F6',
                  }}
                  variant="contained"
                  size="large"
                  onClick={handleClose}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </Slide>
        </Box>
      </Dialog>
    </div>
  );
};

export default CompanyFreemiumModal;
